import { useLang } from "../../contexts/LangContext";
import { useNavigate } from "react-router-dom";
import { useLogSession } from "../../contexts/LogSessionContext";
import {
  convertInCurrentLang,
} from "../../utils/misc";
import { DayRecord, FishConsumptionUnit, MealRecord, PersonRecord } from "../../types/data";
import MealLog from "../../cards/MealLog";
import { useState } from "react";
import { logService } from "../../services/log.service";
import fishSwimmingGif from "../../assets/site-images/fish-swimming.gif";
import { storageService } from "../../services/storage.service";

export default function ReviewLog() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const {
    currentDayRecord,
    currentMealRecord,
    commitMealToDay,
    setNewFishItem,
    setcurrentMealRecord,
    mode,
    exitFallback,
    person,
  } = useLogSession();
  const [isSaving, setIsSaving] = useState(false);

  // Show loading overlay first, before any error checks
  if (isSaving) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur flex items-center justify-center z-50">
        <div className="p-8 flex flex-col items-center gap-4">
          <img src={fishSwimmingGif} alt="Loading..." className="w-40 h-auto" />
          <p className="text-xl font-semibold text-gray-700">{t("reviewLog_Saving")}</p>
        </div>
      </div>
    );
  }

  if (!currentMealRecord || !currentDayRecord) {
    return (
      <div className="mt-8 p-8 text-red-600 px-scr flex flex-col items-center justify-center gap-4">
        <span>{t("reviewLog_MissingRecord")}</span>
        <button
          className="button-xs button-bg-grad-2 mt-4"
        onClick={() => navigate(exitFallback || mode === "survey" ? "/survey/person-list" : "/user")}>
          {t("reviewLog_CloseSurvey")}
        </button>
      </div>
    );
  }

  const verifySaved = async (retries: number = 3): Promise<boolean> => {
    // Safety checks
    if (!person || !currentDayRecord || !currentMealRecord) {
      logService.warn('Review Log', 'verifySaved: Missing required data', {
        hasPerson: !!person,
        hasDayRecord: !!currentDayRecord,
        hasMealRecord: !!currentMealRecord
      });
      return false;
    }

    try {
      // Read fresh data from storage to verify the save
      const userData = await storageService.getUserData();
      
      if (!userData || !userData.persons) {
        logService.warn('Review Log', 'verifySaved: No user data found in storage');
        return false;
      }
      
      // Find the person record in storage
      const personRecord = userData.persons.find((p: PersonRecord) => p.id === person.id);
      
      if (!personRecord) {
        logService.warn('Review Log', 'verifySaved: Person not found in storage', {
          personId: person.id
        });
        return false;
      }
      
      // Find the day record
      const dayRecord = personRecord.records?.find((day: DayRecord) => day.date === currentDayRecord.date);
      if (!dayRecord) {
        logService.warn('Review Log', 'verifySaved: Day record not found', {
          date: currentDayRecord.date
        });
        
        if (retries > 0) {
          logService.info('Review Log', `verifySaved: Retrying (${retries} attempts left)...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          return verifySaved(retries - 1);
        }
        return false;
      }
      
      // Find the meal record
      const mealRecord = dayRecord.meals?.find((meal: MealRecord) => 
        meal.meal_type === currentMealRecord.meal_type && 
        meal.id === currentMealRecord.id
      );
      
      if (mealRecord) {
        logService.info('Review Log', 'verifySaved: Meal successfully verified in storage');
        return true;
      }
      
      logService.warn('Review Log', 'verifySaved: Meal not found in storage', {
        mealType: currentMealRecord.meal_type,
        mealId: currentMealRecord.id
      });
      
      if (retries > 0) {
        logService.info('Review Log', `verifySaved: Retrying (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return verifySaved(retries - 1);
      }
      
      return false;
    } catch (error) {
      logService.error('Review Log', 'verifySaved: Error verifying saved data', {
        error: error instanceof Error ? error.message : String(error)
      });
      
      if (retries > 0) {
        logService.info('Review Log', `verifySaved: Retrying after error (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return verifySaved(retries - 1);
      }
      
      return false;
    }
  };

  const handleItemEdit = (item: FishConsumptionUnit) => {
    setNewFishItem(item);
    navigate("../which-recipe");
  };

  const handleItemDelete = (item: FishConsumptionUnit) => {
    if (window.confirm(t("reviewFishItem_ConfirmDelete"))) {
      const updatedMeal = {
        ...currentMealRecord,
        fish_items: currentMealRecord.fish_items.filter(
          (i) => i.id !== item.id
        ),
        updated_at: new Date().toISOString(),
      };
      setcurrentMealRecord(updatedMeal);

      // if (updatedMeal.fish_items.length === 0) {
      //   setNewFishItem(null);
      //   navigate("../add-another-fish-item");
      // } else {
      // }
    }
  };

  const handleConfirm = async () => {
    if (currentMealRecord.fish_items.length === 0) {
      const ok = window.confirm(t("reviewLog_NoFishItemsToSaveConfirmToProceed"));
      if (!ok) return;
    }

    setIsSaving(true);
    
    try {
      // Retry loop: Commit -> Wait -> Verify, up to 3 attempts
      let commitSuccess = false;
      const maxCommitAttempts = 3;
      
      for (let attempt = 1; attempt <= maxCommitAttempts; attempt++) {
        logService.info('Review Log', `Commit attempt ${attempt}/${maxCommitAttempts}`);
        
        try {
          // Commit meal to storage
          await commitMealToDay(true);
          logService.info('Review Log', 'commitMealToDay completed');
          
          // Wait 2 seconds for save animation and storage flush
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Verify the save
          logService.info('Review Log', 'Verifying save operation...');
          const saveVerified = await verifySaved();
          
          if (saveVerified) {
            logService.info('Review Log', `Save verified successfully on attempt ${attempt}`);
            commitSuccess = true;
            break; // Success! Exit retry loop
          } else {
            logService.warn('Review Log', `Verification failed on attempt ${attempt}`);
            
            if (attempt < maxCommitAttempts) {
              logService.info('Review Log', `Retrying commit (${maxCommitAttempts - attempt} attempts remaining)...`);
              // Add extra delay before retry
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        } catch (error) {
          logService.error('Review Log', `Commit attempt ${attempt} threw error`, {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
          });
          
          if (attempt < maxCommitAttempts) {
            logService.info('Review Log', `Retrying after error (${maxCommitAttempts - attempt} attempts remaining)...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // Last attempt failed, throw error
            throw error;
          }
        }
      }
      
      if (!commitSuccess) {
        const errorMsg = t('reviewLog_SaveVerificationFailed');
        logService.error('Review Log', errorMsg, {
          personId: person?.id,
          date: currentDayRecord?.date,
          mealType: currentMealRecord?.meal_type,
          mealId: currentMealRecord?.id,
          attempts: maxCommitAttempts
        });
        alert(errorMsg);
        setIsSaving(false);
        return;
      }

      logService.info('Review Log', 'Save operation completed and verified');
      
      // Navigate first (don't clear state until after to prevent error message)
      navigate("../submitted");
      
      // Clear session state after navigation starts
      setTimeout(() => {
        setNewFishItem(null);
        setcurrentMealRecord(null);
      }, 100);
    } catch (error) {
      logService.error('Review Log', 'Failed to save meal after all retries', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        mealId: currentMealRecord?.id
      });
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full grow flex flex-col items-center justify-center gap-6 text-center px-scr pt-8 relative">
      <h1 className="text-2xl font-bold text-rose-700">
        {t("reviewLog_Heading")}
      </h1>

      <div className="rounded-3xl w-full p-4 text-center text-lg bg-gray-100/50 backdrop-blur">
        <p className="text-xl font-bold text-blue-700">
          {t("logHistoryDateLog_Heading").replace(
            "{date}",
            convertInCurrentLang(lang, currentDayRecord.date || "")
          )}
        </p>

        <MealLog meal={currentMealRecord} allowDelete={true} onDelete={handleItemDelete} />
      </div>

      <div className="flex flex-col items-center max-w-md gap-4 w-full px-4 my-4 mb-8">
        <button
          className="w-full button-base button-bg-grad-2 !text-xl grow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => navigate("../add-another-fish-item")}
          disabled={isSaving}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          <span></span>
          {t("reviewLog_AddAnotherFishItem")}
        </button>
        <button
          onClick={handleConfirm}
          className="w-full button-base button-bg-grad-green !text-xl grow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSaving}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m4.5 12.75 6 6 9-13.5"
            />
          </svg>
          <span></span>
          {t("reviewLog_Confirm")}
        </button>
      </div>
    </div>
  );
}
